import { observer } from 'mobx-react-lite'
import { DialogProps } from '@mui/material'
import { Title2 } from '../../../../feature/localization/component/atom/Title2'
import { AppBox, AppBoxProps } from '../molecule/AppBox'
import { AppUnstyledButton } from '../molecule/button/AppUnstyledButton'
import { IsTrue } from '../../../../model/Boolean'
import { TranslatableText, TranslatableTextVariantProps } from '../../../../feature/localization/component/atom/TranslatableText'
import { AppDialogContent, AppDialogContentProps } from '../molecule/dialog/AppDialogContent'
import { ChildrenProps } from '../../../../model/React'
import { handlePassedSx } from '../../../../feature/theme/model/Sx'
import { AppDialog } from '../molecule/dialog/AppDialog'
import { SxHelperStyle } from '../../../../feature/theme/style/styleUtilities'
import CloseIcon from '@mui/icons-material/Close'
import { ColorTokens } from 'src/feature/theme/model/Palette'

export interface ModalProps extends ChildrenProps {
  modalProps: Omit<DialogProps, 'open'> & { open: IsTrue }
  disableCloseButton?: boolean
  titleProps?: TranslatableTextVariantProps
  subtitleProps?: TranslatableTextVariantProps
  headerIcon?: React.ReactNode
  headerContent?: React.ReactNode
  dialogContentProps?: AppDialogContentProps
  topContent?: React.ReactNode
  topProps?: AppBoxProps
}

export const ModalWindow = observer((props: ModalProps) => {
  const {
    children,
    modalProps: {
      open,
      maxWidth = 'sm',
      transitionDuration = 200,
      scroll = 'paper',
      PaperProps: {
        sx: PaperSx = [],
        ...restPaperProps
      } = {},
      ...restRootProps
    } = {},
    topProps: {
      sx: topSx = [],
      mb: topMb = 20,
      ...restTopProps
    } = {},
    dialogContentProps: {
      sx: dialogContentSx = [],
      ...restDialogContentProps
    } = {},
    titleProps,
    subtitleProps,
    headerIcon,
    headerContent,
    topContent,
    disableCloseButton,
  } = props

  const hasStructuredHeader = Boolean(titleProps || subtitleProps || headerIcon)

  const renderedTopContent = headerContent || (hasStructuredHeader ? (
    <AppBox sx={styles.headerContent}>
      {headerIcon && (
        <AppBox sx={styles.headerIconWrapper}>
          {headerIcon}
        </AppBox>
      )}

      <AppBox sx={styles.headerTextContent}>
        {titleProps && (
          <Title2
            {...titleProps}
            my={0}
          />
        )}
        {subtitleProps && (
          <TranslatableText
            {...subtitleProps}
            variant={subtitleProps.variant || 'body2'}
            component={subtitleProps.component || 'p'}
          />
        )}
      </AppBox>
    </AppBox>
  ) : topContent)

  return (
    <AppDialog
      TransitionComponent={props.modalProps.TransitionComponent}
      maxWidth={maxWidth}
      open={Boolean(open)}
      transitionDuration={transitionDuration}
      scroll={scroll}
      PaperProps={{
        sx: [
          styles.paper,
          ...handlePassedSx(PaperSx),
        ],
        ...restPaperProps,
      }}
      {...restRootProps}
    >
      <AppBox
        mb={topMb}
        sx={[
          styles.top,
          ...handlePassedSx(topSx),
        ]}
        {...restTopProps}
      >
        {renderedTopContent}

        {!disableCloseButton && (
          <AppUnstyledButton onClick={props.modalProps.onClose} sx={styles.closeButton} type={'button'}>
            <CloseIcon fontSize='small' color='inherit' />
          </AppUnstyledButton>
        )}

      </AppBox>

      <AppDialogContent
        sx={[
          styles.content,
          ...handlePassedSx(dialogContentSx),
        ]}
        {...restDialogContentProps}
      >
        {children}
      </AppDialogContent>
    </AppDialog>
  )
})

const styles = {
  paper: {
    paddingTop: {
      xs: 10,
      md: 20,
    },
    paddingBottom: {
      xs: 10,
      md: 20,
    },
    paddingLeft: {
      xs: 15,
      md: 20,
    },
    paddingRight: {
      xs: 15,
      md: 20,
    },
    width: 1,
    margin: {
      xs: '10px',
      md: '30px',
    },
    backgroundColor: 'background.dark',
  },
  top: {
    ...SxHelperStyle.flexCenterX,
    justifyContent: 'space-between',
    width: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
    flex: 1,
  },
  headerIconWrapper: {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ColorTokens.primary100,
  },
  headerTextContent: {
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    overflow: 'hidden',
  },
  closeButton: {
    ml: 'auto',
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    justifyContent: 'center',
    border: '2px solid transparent',
    borderRadius: '999px',
    transition: 'border-color 0.3s ease, background-color 0.3s ease',
    '&:hover': {
      borderColor: ColorTokens.neutral600,
      backgroundColor: 'background.paper',
    },
    color: ColorTokens.neutral600,
    width: '1.2rem',
    height: '1.2rem',
  },
  content: {
    padding: 0,
    margin: 0,
    width: 1,
  },
}
